 
import React from "react";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { MultiUserSelect } from "@/components/shared/selects/MultiUserSelect";
import type { SimpleFieldConfig } from "../types";
import type { FilterState } from "@/components/shared/types";

interface SelectFilterProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

/** Removes a key from filters or sets it, then calls onFilterChange. */
function applyStringFilter(
  fieldId: string,
  value: string,
  filters: FilterState,
  onFilterChange: (filters: FilterState) => void
) {
  const newFilters = { ...filters };
  if (value === "" || value === "all") {
    delete newFilters[fieldId];
  } else {
    newFilters[fieldId] = value;
  }
  onFilterChange(newFilters);
}

export function CompanySelectFilter({ fieldId, fieldConfig, filters, onFilterChange }: SelectFilterProps) {
  return (
    <div key={fieldId} className="w-full min-w-[320px]">
      <CompanySelectWithModal
        value={(filters[fieldId] as string | undefined) || ""}
        onValueChange={(value) => applyStringFilter(fieldId, value, filters, onFilterChange)}
        placeholder={`Filter by ${fieldConfig.label}...`}
        expandInline={true}
        expandInlineVariant="replace"
        centerOnScreen={false}
      />
    </div>
  );
}

export function ClientSelectFilter({ fieldId, fieldConfig, filters, onFilterChange }: SelectFilterProps) {
  return (
    <div key={fieldId} className="w-full">
      <ClientSelectWithModal
        value={(filters[fieldId] as string | undefined) || ""}
        onValueChange={(value) => applyStringFilter(fieldId, value, filters, onFilterChange)}
        placeholder={`Filter by ${fieldConfig.label}...`}
      />
    </div>
  );
}

export function UserSelectFilter({ fieldId, fieldConfig, filters, onFilterChange }: SelectFilterProps) {
  const value: string[] = Array.isArray(filters[fieldId]) ? (filters[fieldId]) : [];
  return (
    <div key={fieldId} className="w-full">
      <MultiUserSelect
        currentUserIds={value}
        onUsersSelect={async (userIds) => {
          const newFilters = { ...filters };
          if (!userIds || userIds.length === 0) {
            delete newFilters[fieldId];
          } else {
            newFilters[fieldId] = userIds;
          }
          onFilterChange(newFilters);
        }}
        placeholder={`Filter by ${fieldConfig.label}...`}
        showUnassigned={false}
        showAllUsers={false}
        maxUsers={10}
        expandInlineVariant="replace"
        meOptionLabel="Me"
        meOptionDescription="Filter to records tied to you"
      />
    </div>
  );
}
